import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import type { OfferPageFilterType, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import moment from 'moment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'LuxKit/Button/TextButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import GeoContext from 'contexts/geoContext'
import { FLIGHTS_MIN_LEAD_TIME_DAYS, FLIGHTS_MAX_LEAD_TIME_DAYS, FlightsClassTypes, isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import { getFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { getOfferDestinationAirport } from 'selectors/offerPage/offerPageSelectors'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useIntl } from 'react-intl'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import { isOfferPageFullyPaidWithPoints } from 'lib/offer/isOfferPageFullyPaidWithPoints'
import OfferPagePaidEntirelyWithPointsLabel from 'components/OfferPage/Loyalty/OfferPagePaidEntirelyWithPointsLabel'
import ReturnFareFreeLabel from 'components/Common/Labels/ReturnFareFreeLabel'
import FlashHotelOneWayFlightSaleLabel from './FlashHotelOneWayFlightSaleLabel'
import { useOfferPageBundledBurnPoints } from 'hooks/useOfferPageBurnPoints'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import RewardBundlePriceDisplay from './RewardBundlePriceDisplay'
import { computeRewardBundlePrice } from 'lib/maldivesCharter/classicRewardPricing'
import getEffectiveHotelFlightPrice from 'lib/offer/getEffectiveHotelFlightPrice'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'

interface Props {
  offer: App.Offer
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  isLoggedIn: boolean
  checkIn?: string
  currency: string
  room: OfferPageRoom
  hasFlights: boolean
  packageView: App.OfferPackageOptionView
  isFetching?: boolean
  defaultDepositAmountPercentage: number
  showChangeAirport?: boolean
  flightPrice?: number
  /** Reward-mode points portion to display on this room (already split per-room by caller). */
  rewardPointsForRoom?: number
  /** Reward-mode cash co-pay portion for this room (already split per-room by caller). */
  rewardCashCoPayForRoom?: number
  /** Whether reward inventory is available for the dates. */
  rewardAvailable?: boolean
  /** Page-level fare class — if a reward variant is selected and inventory exists, the
   *  cash flight portion is replaced with `{points} + {cash}`. */
  fareClass?: FareClassWithReward
  priceMode: OfferPagePriceMode
  charterBundleRoute?: App.CharterBundleRoute
  canShowDiscount?: boolean
}

function FlashHotelIndividualRoomsPriceDetails(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    checkIn,
    currency,
    room,
    hasFlights,
    packageView,
    isFetching,
    defaultDepositAmountPercentage,
    showChangeAirport = true,
    flightPrice,
    rewardPointsForRoom,
    rewardCashCoPayForRoom,
    rewardAvailable,
    fareClass,
    priceMode,
    charterBundleRoute,
    canShowDiscount = true,
  } = props
  const { loading: charterFlightLoading, hasFeederFlightPriced, hasReturnFareFree, oneWayBundle, rewardPreviewRate: flightRewardPreviewRate } = useOfferPageFlightPrice()
  const isRewardSelected = !!fareClass && isRewardFareClass(fareClass)
  const showRewardPrice = isRewardSelected && !!rewardAvailable && rewardPointsForRoom !== undefined && rewardCashCoPayForRoom !== undefined
  const rewardPreviewRate = isRewardSelected ? flightRewardPreviewRate : undefined
  const seatEligiblePax = (room.adults ?? 0) + (room.children ?? 0) + (room.teenagers ?? 0)
  let rewardPoints = rewardPointsForRoom
  let rewardCashCoPay = rewardCashCoPayForRoom
  if (!showRewardPrice && rewardPreviewRate) {
    rewardPoints = rewardPreviewRate.pointsPerPerson * seatEligiblePax
    rewardCashCoPay = rewardPreviewRate.cashPerPerson * seatEligiblePax
  }

  const intl = useIntl()
  const {
    package: pkg,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = packageView

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const offerPageDispatch = useOfferPageDispatch()
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: pkg.duration,
    charterBundleRoute,
    fareClass: fareClass ?? FlightsClassTypes.ECONOMY,
  })

  const effectiveFlightPrice = getEffectiveHotelFlightPrice({
    charterBundleRoute,
    checkIn,
    flightOriginCode: flightOrigin?.code,
    offer: offer as App.HotelOffer,
    totalFlightPrice: flightPrice,
  })

  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice
  const shouldShowCharterFlightErrorMessage = missingCharterFlightPrice && !charterFlightLoading
  const feederOriginAirportCode = getFeederOriginAirportCode(charterBundleRoute, flightOrigin)
  // Show the feeder caption only when at least one charter leg carries a positive
  // `feederEstimatedPrice` for the picked dates. Otherwise drop back to the generic
  // "& flights from" caption — charter is still bundled, but no feeder leg is priced.
  const isFeederIncluded = !!feederOriginAirportCode && hasFeederFlightPriced
  const showReturnFareFree = (packageView.hasReturnFareFree ?? hasReturnFareFree) && !feederOriginAirportCode

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = canShowDiscount && hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = canShowDiscount && bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])

  const departureTooEarly = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInMoment])

  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  }, [checkInMoment])

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const saleUnit = priceMode === 'perPerson' ? 'person' : 'total'
  const totalMemberPrice = useMemo(() => {
    if (hotelMemberPrice > 0) {
      return hotelMemberPrice + (propertyFees ?? 0)
    }
    return 0
  }, [hotelMemberPrice, propertyFees])
  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, (showMemberPrice ? hotelMemberPrice : hotelPrice))

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        packageView,
        flightPrice: effectiveFlightPrice,
        skip: !hasFlights || isRewardSelected,
      }),
    ]
  }, [offer, packageView, effectiveFlightPrice, hasFlights, currentRegionCode, isRewardSelected])

  const convertToDisplay = getPriceDisplayFunction(priceMode, [room])
  const hotelLoyaltyProductType = getLoyaltyProductType(offer as App.HotelOffer) ?? 'hotel_flash'
  const hotelBurnMargin = getHotelEncryptedMargin(offer.type, pkg)
  const shouldUseSplitBurn = effectiveFlightPrice > 0 && hasFlights
  const burnResult = useOfferPageBundledBurnPoints({
    hotelPrice,
    hotelLoyaltyProductType,
    hotelBurnMargin,
    flightPrice: effectiveFlightPrice,
    shouldUseSplitBurn,
  })
  const { enabled: showPointsPrice, pointsToBurn: burnPointsToApply, priceInPoints: burnPriceInPoints, hideZeroCash: burnHideZeroCash } = burnResult
  const shouldShowPaidEntirelyWithPointsLabel = isOfferPageFullyPaidWithPoints(burnPointsToApply, burnPriceInPoints, burnHideZeroCash)
  const renderBurnPriceDisplay = (displayPrice: number, displayMemberPrice: number) => {
    if (!showPointsPrice) {
      return null
    }

    return (
      <OfferPageBurnPriceDisplay
        totalPrice={displayPrice}
        pointsToApply={burnPointsToApply}
        priceInPoints={burnPriceInPoints}
        pointsHideZeroCash={burnHideZeroCash}
        memberPrice={displayMemberPrice}
        size="S"
        saleUnit={saleUnit}
      />
    )
  }

  return (
    <Group direction="vertical" gap={12}>
      {isDepositEnabled && <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds} />}
      <Group direction="vertical" gap={12}>
        {!hasFlights && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="pricing">
              {shouldShowDiscount && <PriceRowValueDiscount discountPercentage={highestDiscountPercent} />}
              {shouldShowPaidEntirelyWithPointsLabel && <OfferPagePaidEntirelyWithPointsLabel />}
              <LuxPlusOfferVoucherCallout offer={offer} mode="offer" align="start" />
              <PriceRowPriceCaption>
                {intl.formatMessage(
                  {
                    id: 'uSMBlCaFoJrYEnxCv7tm',
                    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
                    description: 'Duration in nights for individual room hotel-only pricing',
                  },
                  { count: pkg.duration },
                )}
              </PriceRowPriceCaption>
              {renderBurnPriceDisplay(convertToDisplay(totalPrice), convertToDisplay(totalMemberPrice))}
              {!showPointsPrice &&
                <LuxPlusPriceStack
                  size="S"
                  price={convertToDisplay(totalPrice)}
                  memberPrice={convertToDisplay(totalMemberPrice)}
                  saleUnit={saleUnit}
                  offerConfig={offer.luxPlus}
                />}
              <PriceRowTaxesAndFees />
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description={intl.formatMessage({
                    id: 'c49bMGV6K-aAi1z169XQ',
                    defaultMessage: '+ Extra guest surcharge',
                    description: 'Description for extra guest surcharge on the offer detail page',
                  })}
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
              <PriceRowAgentHubCommission
                offerId={offer.id}
                productType={offer.productType}
                packageId={pkg.id}
                offerTotal={hotelPrice}
                kind="plain"
                offer={offer}
              />
            </Group>
            <CSSBreakpoint min="desktop">
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={(!checkIn || hasFlights) ? 'estimate' : 'exact'}
                pointsToApply={burnPointsToApply}
                burnPriceInPoints={burnPriceInPoints}
              />
            </CSSBreakpoint>
          </Group>
        )}

        {hasFlights && (
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4} data-testid="flights-pricing">
              {(() => {
                if (shouldShowCharterFlightErrorMessage) {
                  return <MessageBanner kind="critical">
                    {intl.formatMessage({
                      id: '2frCP-voXkEo2-v8hOji',
                      defaultMessage: 'Failed to find charter flight data',
                      description: 'Error message when charter flight data cannot be found',
                    })}
                  </MessageBanner>
                }
                else if (flightOrigin?.code === flightDestination?.code) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      {intl.formatMessage(
                        {
                          id: 'ui_0SxB-9Lc52kGMUvbg',
                          defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
                          description: 'Message when no flights available from selected airport',
                        },
                        {
                          airportName: flightOrigin?.name ?? '',
                        },
                      )}
                    </PriceRowCaption>
                    {showChangeAirport && <TextButton
                      kind="tertiary"
                      onClick={onToggleFilter}
                      data-filter="airport"
                    >
                      {intl.formatMessage({
                        id: 'FJeKASDKevfTCPbRvSz7',
                        defaultMessage: 'Change airport',
                        description: 'Button text to change the selected departure or arrival airport in the flight form',
                      })}
                    </TextButton>}
                  </Group>
                }
                else if (departureTooEarly) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      {intl.formatMessage({
                        id: '-kSp-mFmIA4X5iA5WKLd',
                        defaultMessage: 'Departure date is too soon.',
                        description: 'Message when departure date is too soon for flights',
                      })}
                    </PriceRowCaption>
                  </Group>
                }
                else if (departureTooFarInFuture) {
                  return <Group direction="vertical" gap={8}>
                    <PriceRowCaption>
                      {intl.formatMessage({
                        id: 'SjlpFqk2nfjBfQqW7_O9',
                        defaultMessage: 'Flights can only be booked up to 11 months in advance.',
                        description: 'Notification message displayed when departure date is too far in future',
                      })}
                    </PriceRowCaption>
                  </Group>
                }
                else if (effectiveFlightPrice) {
                  // Reward-mode display: replace the cash flight portion with `{points} + {cash}`
                  // (room hotel + this room's pax-share of reward cash co-pay), mirroring the
                  // single-room offer summary. When LuxPlus member hotel pricing exists, the
                  // member price becomes the headline and the non-member price renders below.
                  let roomNightsCaption: string
                  if (isFeederIncluded) {
                    roomNightsCaption = intl.formatMessage(
                      {
                        id: 'oJywh3Zl_eURnEcbimf8',
                        defaultMessage: '{count, plural, one {# night} other {# nights}} & {code} return flights from',
                        description: 'Charter bundle price caption showing nights and feeder airport code for non-Melbourne departures',
                      },
                      { count: pkg.duration, code: feederOriginAirportCode },
                    )
                  } else {
                    roomNightsCaption = intl.formatMessage(
                      {
                        id: 'wzCeR1MoNZxwTEj7fg3y',
                        defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
                        description: 'Duration in nights with flights for individual room pricing',
                      },
                      { count: pkg.duration },
                    )
                  }
                  if (rewardPoints !== undefined && rewardCashCoPay !== undefined) {
                    const rewardPrice = computeRewardBundlePrice({
                      hotelPrice: totalPrice,
                      hotelMemberPrice: totalMemberPrice > 0 ? totalMemberPrice : undefined,
                      rewardPointsTotal: rewardPoints,
                      rewardCashCoPayTotal: rewardCashCoPay,
                    })
                    const isMemberReward = rewardPrice.memberCash !== undefined
                    const rewardCashHeadline = rewardPrice.memberCash ?? rewardPrice.cash
                    return <Group direction="vertical" gap={4}>
                      <FlashHotelOneWayFlightSaleLabel charterBundleRoute={charterBundleRoute} oneWayBundle={oneWayBundle} fareClass={fareClass} departureDate={charterDepartDate} />
                      <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                        {roomNightsCaption}
                      </PriceRowPriceCaption>
                      <RewardBundlePriceDisplay
                        points={convertToDisplay(rewardPoints)}
                        cash={convertToDisplay(rewardCashHeadline)}
                        nonMemberCash={isMemberReward ? convertToDisplay(rewardPrice.cash) : undefined}
                        unitLabel={priceMode === 'perPerson'
                          ? intl.formatMessage({ id: '3eORxyn3qh2dwEg3D6jH', defaultMessage: 'person*', description: 'Per-person sale unit label for reward pricing on the multi-room individual room tile' })
                          : intl.formatMessage({ id: 'tf03xF_7vIgjyZTiO2JM', defaultMessage: 'total*', description: 'Total sale unit label for reward pricing on the multi-room individual room tile' })}
                      />
                      <PriceRowTaxesAndFees />
                      {showIncludedGuestsExceeded && (
                        <AdditionalGuestsPopup
                          complex={false}
                          description={intl.formatMessage({
                            id: 'c49bMGV6K-aAi1z169XQ',
                            defaultMessage: '+ Extra guest surcharge',
                            description: 'Description for extra guest surcharge on the offer detail page',
                          })}
                          modalContent={pkg.roomPolicyDescription}
                        />
                      )}
                      <PriceRowAgentHubCommission
                        offerId={offer.id}
                        productType={offer.productType}
                        packageId={pkg.id}
                        offerTotal={hotelPrice}
                        kind="plain"
                        offer={offer}
                      />
                    </Group>
                  }
                  return <Group direction="vertical" gap={4}>
                    {shouldShowPaidEntirelyWithPointsLabel && <OfferPagePaidEntirelyWithPointsLabel />}
                    {showReturnFareFree && <ReturnFareFreeLabel />}
                    <Group direction="vertical" horizontalAlign="center" noDisplayWhenEmpty>
                      <LuxPlusOfferVoucherCallout offer={offer} mode="offer" />
                    </Group>
                    <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                      {roomNightsCaption}
                    </PriceRowPriceCaption>
                    {renderBurnPriceDisplay(
                      convertToDisplay(totalPrice + effectiveFlightPrice),
                      convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice + effectiveFlightPrice : 0),
                    )}
                    {!showPointsPrice && <LuxPlusPriceStack
                      size="S"
                      price={convertToDisplay(totalPrice + effectiveFlightPrice)}
                      memberPrice={convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice + effectiveFlightPrice : 0)}
                      saleUnit={priceMode === 'perPerson' ? 'person*' : 'total*'}
                      offerConfig={offer.luxPlus}
                    />}
                    <PriceRowTaxesAndFees />
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description={intl.formatMessage({
                          id: 'c49bMGV6K-aAi1z169XQ',
                          defaultMessage: '+ Extra guest surcharge',
                          description: 'Description for extra guest surcharge on the offer detail page',
                        })}
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                    <PriceRowAgentHubCommission
                      offerId={offer.id}
                      productType={offer.productType}
                      packageId={pkg.id}
                      offerTotal={hotelPrice}
                      kind="plain"
                      offer={offer}
                    />
                  </Group>
                }
                else {
                  return <Group direction="vertical" gap={4}>
                    {shouldShowPaidEntirelyWithPointsLabel && <OfferPagePaidEntirelyWithPointsLabel />}
                    <Group direction="vertical" horizontalAlign="center" noDisplayWhenEmpty>
                      <LuxPlusOfferVoucherCallout offer={offer} mode="offer" />
                    </Group>
                    <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                      {intl.formatMessage(
                        {
                          id: 'J6913LKphV0-WMfevGCn',
                          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
                          description: 'Duration in nights for individual room pricing with flight icon',
                        },
                        { count: pkg.duration },
                      )}
                    </PriceRowPriceCaption>
                    {renderBurnPriceDisplay(
                      convertToDisplay(totalPrice),
                      convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice : 0),
                    )}
                    {!showPointsPrice && <LuxPlusPriceStack
                      size="S"
                      price={convertToDisplay(totalPrice)}
                      memberPrice={convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice : 0)}
                      saleUnit=""
                      offerConfig={offer.luxPlus}
                    />}
                    <Caption variant="large">
                      {intl.formatMessage({
                        id: 'ycXWBKNkdmR-ZAXMqz2Z',
                        defaultMessage: '& flights',
                        description: 'Caption indicating flights are included in the flight search or booking flow',
                      })}
                    </Caption>
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description={intl.formatMessage({
                          id: 'c49bMGV6K-aAi1z169XQ',
                          defaultMessage: '+ Extra guest surcharge',
                          description: 'Description for extra guest surcharge on the offer detail page',
                        })}
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                    <PriceRowAgentHubCommission
                      offerId={offer.id}
                      productType={offer.productType}
                      packageId={pkg.id}
                      offerTotal={hotelPrice}
                      kind="plain"
                      offer={offer}
                    />
                  </Group>
                }
              })()}
              <AtolProtectedTooltip />
            </Group>
            <CSSBreakpoint min="desktop">
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={(!checkIn || hasFlights) ? 'estimate' : 'exact'}
                pointsToApply={burnPointsToApply}
                burnPriceInPoints={burnPriceInPoints}
              />
            </CSSBreakpoint>
          </Group>
        )}
        <LoadingIndicator visible={!!isFetching} floating opaque />
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  priceMode: state.priceMode,
  charterBundleRoute: state.charterBundleRoute,
  flightDestination: getOfferDestinationAirport(state, appState),
  fareClass: state.fareClass,
}), FlashHotelIndividualRoomsPriceDetails)
